import useTranslator from '@hooks/use-translator'
import { useAuth } from '@stores/auth-store'
import {
  GET_ORGANIZATION,
  ORGANIZATION_UPDATE_LOGO_ENDPOINT,
  ORGANIZATION_UPDATE_NAME_ENDPOINT,
  SERVER_URL,
} from '@pma/utils/api-routes'
import { ACCESS_TOKEN } from '@pma/utils/constants'
import { useMutation, useQuery } from '@tanstack/react-query'
import axios from 'axios'
import { parseCookies } from 'nookies'
import { Card, CardContent } from '@components/common/card'
import { Avatar, AvatarFallback, AvatarImage } from '@components/common/avatar'

import { DictType } from '@pma/types'
import PmsLoader from '@components/common/loader'
import { useRef, useState } from 'react'
import { toast } from '@hooks/use-toast'
import { Button } from '@components/common/button'
import { CheckCircleIcon, ChevronDown, Edit, Edit2 } from 'lucide-react'
import { Input } from '@components/common/input'
import { Link } from 'react-router-dom'
import { USERS_PAGE_ROUTE } from '@pma/utils/page-routes'
import { cn } from '@pma/utils/helpers'
import LocaleSwitcher from '@pma/components/layout/locale-switcher'
import { locales } from '@pma/locale/dictionaries'

const OrganizationProfile = () => {
  const [uploadedImage, setUploadedImage] = useState<string | null>(null)
  const [avatar, setAvatar] = useState<File>()
  const { translate } = useTranslator()

  const { authConfig } = useAuth()
  const accessToken = parseCookies()[ACCESS_TOKEN]

  const avatarInputRef = useRef<HTMLInputElement>(null)

  const orgId = authConfig?.last_organization_id

  const workSpaceDetails = async () => {
    const response = await axios.get(
      SERVER_URL + GET_ORGANIZATION + `/${orgId}`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    )

    return response?.data
  }

  const {
    data: organization,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ['workspace-details', orgId],
    queryFn: workSpaceDetails,
  })

  const handleFileInputChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0]
    if (file) {
      // Read the selected file and convert it to a data URL
      const reader = new FileReader()
      reader.onloadend = () => {
        setUploadedImage(reader.result as string)
      }
      reader.readAsDataURL(file)
      setAvatar(file)
    }
  }

  const uploadOrganizationLogo = async (r: File) => {
    const formData = new FormData()
    formData.append('content', r, r.name)
    formData.append('organization_id', orgId)
    const response = await axios.post(
      SERVER_URL + ORGANIZATION_UPDATE_LOGO_ENDPOINT,
      formData,
      {
        headers: {
          accept: 'application/json',
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${accessToken}`,
        },
      },
    )

    return response.data
  }

  const updateOrganizationLogo = useMutation({
    mutationKey: ['update-organization-logo'],
    mutationFn: uploadOrganizationLogo,
    onSuccess: (data: DictType) => {
      toast({
        title: translate('Success'),
        description: translate('Organization logo has been updated'),
        variant: 'success',
      })

      setUploadedImage(null)
      setAvatar(undefined)
      refetch()
    },
    onError: (error: DictType) => {
      const errorMessage = error?.response?.data?.message

      toast({
        title: translate('Error'),
        description: translate(
                errorMessage ||
                  `Unable to update organization logo at this time`,
              ),
        variant: 'error',
      })
    },
  })
  return (
    <Card className='p-0 border-none shadow-none min-h-[200px]'>
      {isLoading ? (
        <PmsLoader />
      ) : (
        <CardContent className='flex items-center gap-4'>
          <div className='relative'>
            {uploadedImage ? (
              <Avatar className='h-24 md:h-44 w-24 md:w-44'>
                <AvatarImage src={uploadedImage} />
                <AvatarFallback className='font-bold'>ROG</AvatarFallback>
              </Avatar>
            ) : (
              <Avatar className='h-24 md:h-44 w-24 md:w-44'>
                <AvatarImage
                  src={
                    organization?.owner?.company_logo?.url
                      ? organization?.owner?.company_logo.url
                      : `${process.env.PUBLIC_URL}/user-profile.svg`
                  }
                />
                <AvatarFallback className='font-bold'>ROG</AvatarFallback>
              </Avatar>
            )}

            {
              <div className='absolute top-0 right-0'>
                {uploadedImage ? (
                  <Button
                    size={'icon'}
                    variant='secondary'
                    title={translate('Save organization logo')}
                    loading={updateOrganizationLogo.isPending}
                    disabled={updateOrganizationLogo.isPending}
                    className='rounded-full h-7 w-7'
                  >
                    <CheckCircleIcon
                      onClick={() =>
                        avatar && updateOrganizationLogo.mutate(avatar)
                      }
                      size={16}
                    />
                  </Button>
                ) : (
                  <Button
                    size={'icon'}
                    variant='default'
                    title={translate('Edit Or')}
                    className='rounded-full h-7 w-7'
                  >
                    <Edit
                      onClick={() => avatarInputRef.current?.click()}
                      size={16}
                    />
                  </Button>
                )}
                <label>
                  <Input
                    type='file'
                    accept='image/png, image/gif, image/jpeg'
                    className='hidden'
                    onChange={handleFileInputChange}
                    ref={avatarInputRef}
                  />
                </label>
              </div>
            }
          </div>
          <div className='flex flex-col'>
            <OrganizationNameEditor
              organizationName={organization?.owner?.organization_name}
              refetch={refetch}
            />

            <div className='mt-2 flex items-center text-sm gap-0.5'>
              <h5 className='font-normal'>{translate('Owner')}:</h5>
              <Link
                className='text-primary font-semibold'
                to={USERS_PAGE_ROUTE + `/${organization?.owner_id}`}
              >
                {' '}
                {organization?.owner?.name || '---'}
              </Link>
            </div>

            <div className='mt-2 flex items-center text-sm gap-0.5'>
              <h5 className='font-normal'>{translate('Default Language')}:</h5>
              <LocaleSwitcher
                changeOrg={refetch}
                orgLocale={organization?.owner?.organization_language}
              >
                <span className='text-primary items-center capitalize flex gap-0.5'>
                  {translate(
                    locales.find(
                      (locale) =>
                        locale.shortName.toLocaleLowerCase() ==
                        organization?.owner?.organization_language,
                    )?.name || '',
                  )}

                  <ChevronDown size={20} />
                </span>{' '}
              </LocaleSwitcher>
            </div>
          </div>
        </CardContent>
      )}
    </Card>
  )
}

export default OrganizationProfile

const OrganizationNameEditor = ({ organizationName, refetch }: any) => {
  const [editMode, setEditMode] = useState(false)
  const [hover, setHover] = useState(false)
  const [updatedValue, setUpdatedValue] = useState<any>(organizationName ?? '')

  const { authConfig } = useAuth()
  const { translate } = useTranslator()

  const accessToken = parseCookies()[ACCESS_TOKEN]

  const orgId = authConfig?.last_organization_id

  const updateOrganizationName = async () => {
    const response = await axios.post(
      SERVER_URL + ORGANIZATION_UPDATE_NAME_ENDPOINT,
      {
        company_name: updatedValue,
        organization_id: orgId,
      },
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
      },
    )

    return response.data
  }

  const updateOrganizationNameMutation = useMutation({
    mutationKey: [`company name`, orgId],
    mutationFn: updateOrganizationName,
    onSuccess: (data) => {
      // Revalidate project details
      refetch()
    },
    onError: (error) => {
      toast({
        title: translate('Error'),
        description: `${translate(`Error occurred while updating`)} organization name`,
        variant: 'error',
      })
    },
  })
  const onSubmit = () => {
    setEditMode(false)
    if (updatedValue !== '' && updatedValue !== organizationName) {
      // Only update when the content is updated
      updateOrganizationNameMutation.mutate()
    }
    return
  }

  return (
    <div
      className={cn(
        'flex items-center justify-start text-center text-black h-fit font-semibold w-full cursor-pointer hover:border-primary hover:border-3',
        updateOrganizationNameMutation.isPending &&
          'font-light text-transparent bg-clip-text bg-gradient-to-r from-black to-slate-500 animate-shimmer',
      )}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={() => setEditMode(true)}
    >
      {!editMode && (
        <div className='relative w-full text-left'>
          {hover && (
            <Edit2
              className='absolute -right-3 -top-3 text-primary hover:border-3'
              size={14}
            />
          )}
          <h3 className='font-semibold'>{organizationName || '---'}</h3>
        </div>
      )}
      {editMode && (
        <input
          className='rounded border font-normal mb-1 bg-white/20 px-2 outline-none focus:border-primary'
          value={updatedValue}
          onChange={(e) => setUpdatedValue(e?.target?.value)}
          autoFocus
          onBlur={() => {
            onSubmit()
          }}
          onKeyDown={(e) => {
            if (e.key !== 'Enter') return
            onSubmit()
          }}
        />
      )}
    </div>
  )
}
